import { Pencil, Trash2 } from "lucide-react";

import {
  beerDutyLabel,
  formatScheduleDate,
  homeAwayLabel,
  timeSlotLabel,
  timeSlotName,
} from "../../domain/schedule";
import type { ScheduleForecastState } from "../../domain/weather";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Card, CardContent } from "@/shared/ui/card";
import { GameWeather } from "./GameWeather";
import {
  scheduleResultBadgeVariant,
  scheduleResultLabel,
} from "./schedulePresentation";
import type { ScheduleEntry } from "./scheduleTypes";
import { actionKeys } from "../../hooks/actionKeys";
import type { IntentStatusView } from "../../hooks/useIntentStatus";
import { EMPTY_INTENT_STATUS } from "../../hooks/useIntentStatus";
import { Spinner } from "../../shared/ui/spinner";

export function ScheduleList({
  title,
  entries,
  empty,
  onOpen,
  onEdit,
  onDelete,
  readOnly = false,
  weatherByEntryId = {},
  onRetryWeather = () => undefined,
  showWeatherAttribution = false,
  actionStatus = EMPTY_INTENT_STATUS,
  weatherRefreshing = false,
}: {
  title: string;
  entries: ScheduleEntry[];
  empty: string;
  onOpen: (entry: ScheduleEntry) => void;
  onEdit?: (entry: ScheduleEntry) => void;
  onDelete?: (entry: ScheduleEntry) => void;
  readOnly?: boolean;
  weatherByEntryId?: Record<string, ScheduleForecastState>;
  onRetryWeather?: () => void;
  showWeatherAttribution?: boolean;
  actionStatus?: IntentStatusView;
  weatherRefreshing?: boolean;
}) {
  const selectionPending =
    actionStatus.countFor(actionKeys.scheduleSelectGroup) > 0;
  return (
    <section aria-labelledby={`${title.toLowerCase()}-heading`}>
      <h2
        id={`${title.toLowerCase()}-heading`}
        className="mb-2 font-mono text-sm font-bold tracking-[0.1em] text-warning uppercase"
      >
        {title}
      </h2>
      {entries.length ? (
        <ol className="m-0 grid list-none gap-2 p-0">
          {entries.map((entry) => {
            const selecting = actionStatus.isPending(
              actionKeys.scheduleSelect(entry.id),
            );
            const selectionUnknown =
              actionStatus.phaseFor(actionKeys.scheduleSelect(entry.id)) ===
              "outcome_unknown";
            return (
              <li key={entry.id}>
                <Card className="overflow-hidden">
                  <CardContent className="grid gap-2 px-3 py-3">
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        className="min-w-0 flex-1 text-left disabled:cursor-not-allowed"
                        disabled={
                          selecting ||
                          selectionPending ||
                          (readOnly && entry.status !== "completed")
                        }
                        aria-busy={selecting || undefined}
                        onClick={() => onOpen(entry)}
                      >
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-foreground-muted">
                          <span className="font-bold text-foreground">
                            {formatScheduleDate(entry.scheduledDate)}
                          </span>
                          <span>
                            {timeSlotLabel(entry.timeSlot)} ·{" "}
                            {timeSlotName(entry.timeSlot)}
                          </span>
                          {entry.status === "active" ? (
                            <Badge variant="warning">LIVE</Badge>
                          ) : null}
                        </div>
                        <div className="mt-1 truncate text-base font-bold">
                          {entry.isHome ? "vs" : "at"} {entry.opponentName}
                        </div>
                        {selecting ? (
                          <div className="mt-1 inline-flex items-center gap-1 font-mono text-[0.65rem] font-bold text-warning">
                            <Spinner decorative className="size-3" />{" "}
                            {selectionUnknown ? "CHECKING…" : "SELECTING…"}
                          </div>
                        ) : null}
                        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-foreground-muted">
                          <span>{entry.field ?? "Field unknown"}</span>
                          <span>{homeAwayLabel(entry.isHome)}</span>
                          <span>Beer: {beerDutyLabel(entry.beerDuty)}</span>
                        </div>
                      </button>
                      {entry.status === "completed" ? (
                        <Badge
                          variant={
                            entry.result
                              ? scheduleResultBadgeVariant(entry)
                              : "neutral"
                          }
                        >
                          {entry.result
                            ? scheduleResultLabel(entry)
                            : "BOX SCORE"}
                        </Badge>
                      ) : (
                        <div className="flex shrink-0 gap-1">
                          {onEdit ? (
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              aria-label="Edit scheduled game"
                              onClick={() => onEdit(entry)}
                            >
                              <Pencil aria-hidden="true" />
                            </Button>
                          ) : null}
                          {onDelete && entry.status === "scheduled" ? (
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              aria-label="Delete scheduled game"
                              onClick={() => onDelete(entry)}
                            >
                              <Trash2 aria-hidden="true" />
                            </Button>
                          ) : null}
                        </div>
                      )}
                    </div>
                    {entry.status !== "completed" && !readOnly ? (
                      <GameWeather
                        state={weatherByEntryId[entry.id]}
                        onRetry={onRetryWeather}
                        refreshing={weatherRefreshing}
                      />
                    ) : null}
                  </CardContent>
                </Card>
              </li>
            );
          })}
        </ol>
      ) : (
        <p className="rounded-[var(--radius-sm)] border border-dashed border-border px-3 py-4 text-sm text-foreground-muted">
          {empty}
        </p>
      )}
      {showWeatherAttribution && entries.length ? (
        <p className="mt-2 mb-0 text-right text-[0.68rem] text-foreground-muted">
          Weather data by{" "}
          <a
            className="font-bold text-primary underline underline-offset-2"
            href="https://open-meteo.com/"
          >
            Open-Meteo
          </a>
        </p>
      ) : null}
    </section>
  );
}
